import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { apiGet, apiSend } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { CaretLeft, CaretRight, CalendarDots } from "@phosphor-icons/react";
import {
  CalendarGrid,
  CalendarSummary,
  DaySidebar,
  CancelSessionDialog,
  SessionMembersDialog,
  DAY_MARKER_LEGEND,
  STATUS_STYLES,
  STATUS_KEYS,
  startOfWeek,
  addDays,
  fmtDate,
} from "@/components/calendar";
import type { CalendarSession, ViewMode } from "@/components/calendar";
import { TherapistPicker } from "@/components/admin/calendar/TherapistPicker";
import { utcToWallClock, todayInZone } from "@/lib/timezone";
import { dayOverrideMarkers, type OverrideRow } from "@/lib/override-periods";

export const Route = createFileRoute("/dashboard/admin/calendar")({
  component: AdminCalendar,
});

function AdminCalendar() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dayNames = t("calendar.days", { returnObjects: true }) as string[];
  const monthNames = t("calendar.months", { returnObjects: true }) as string[];

  // "Today" is the Sarajevo calendar date, held as a local-midnight Date used
  // only as a (year, month, day) container, same as the therapist calendar.
  const today = useMemo(() => {
    const [y, m, d] = todayInZone().split("-").map(Number) as [number, number, number];
    return new Date(y, m - 1, d);
  }, []);

  const [view, setView] = useState<ViewMode>("week");
  const [anchor, setAnchor] = useState(today);
  const [selectedDate, setSelectedDate] = useState<Date | null>(today);
  const [therapistId, setTherapistId] = useState<string | null>(null);
  const [cancelingSession, setCancelingSession] = useState<CalendarSession | null>(null);
  const [membersSession, setMembersSession] = useState<CalendarSession | null>(null);

  const { from, to, days } = useMemo(() => {
    if (view === "week") {
      const monday = startOfWeek(anchor);
      const sunday = addDays(monday, 6);
      const list: Date[] = [];
      for (let i = 0; i < 7; i++) list.push(addDays(monday, i));
      return { from: fmtDate(monday), to: fmtDate(sunday), days: list };
    }
    const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = startOfWeek(firstOfMonth);
    const list: Date[] = [];
    let d = start;
    while (list.length < 42) { list.push(new Date(d)); d = addDays(d, 1); }
    while (list.length > 35 && list[list.length - 7].getMonth() !== anchor.getMonth()) list.splice(-7);
    return { from: fmtDate(list[0]), to: fmtDate(list[list.length - 1]), days: list };
  }, [view, anchor]);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "calendar", from, to, therapistId],
    queryFn: () => {
      const params = new URLSearchParams({ from, to });
      if (therapistId) params.set("therapistId", therapistId);
      return apiGet<{ sessions: CalendarSession[] }>(
        `/api/admin/calendar?${params}`,
        t("admin.calendar.errorLoading"),
      );
    },
    placeholderData: (prev) => prev,
  });

  // Days off and extra hours only make sense for one therapist at a time.
  const { data: overridesData } = useQuery({
    queryKey: ["admin", "therapist", therapistId, "availability-overrides", from, to],
    queryFn: () =>
      apiGet<{ overrides: OverrideRow[] }>(
        `/api/admin/therapists/${therapistId}/availability-overrides?${new URLSearchParams({ from, to })}`,
        "Greška pri učitavanju odmora i izuzetaka",
      ),
    enabled: therapistId !== null,
    placeholderData: (prev) => prev,
  });

  const dayMarkers = useMemo(
    () => (therapistId && overridesData ? dayOverrideMarkers(overridesData.overrides) : undefined),
    [therapistId, overridesData],
  );

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason, applyFee }: { id: string; reason?: string; applyFee?: boolean }) =>
      apiSend(`/api/sessions/${id}/cancel`, "POST", { reason, applyFee }, "Greška pri otkazivanju"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "calendar"] });
      setCancelingSession(null);
      toast.success("Termin otkazan");
    },
  });

  const sessions = data?.sessions ?? [];

  const sessionsByDate = useMemo(() => {
    const map: Record<string, CalendarSession[]> = {};
    for (const s of sessions) {
      // Bucket by the Sarajevo calendar date (the rendered times are Sarajevo
      // too), not the browser's, so near-midnight sessions land in the right cell.
      const key = utcToWallClock(s.scheduledAt).date;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    }
    return map;
  }, [sessions]);

  const goBack = () => {
    if (view === "week") setAnchor(addDays(anchor, -7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
  };
  const goForward = () => {
    if (view === "week") setAnchor(addDays(anchor, 7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));
  };
  const goToday = () => {
    setAnchor(today);
    setSelectedDate(today);
  };

  const title = useMemo(() => {
    if (view === "week") {
      const mon = startOfWeek(anchor);
      const sun = addDays(mon, 6);
      if (mon.getMonth() === sun.getMonth())
        return `${mon.getDate()}. — ${sun.getDate()}. ${monthNames[mon.getMonth()]} ${mon.getFullYear()}`;
      return `${mon.getDate()}. ${monthNames[mon.getMonth()].substring(0, 3)} — ${sun.getDate()}. ${monthNames[sun.getMonth()].substring(0, 3)} ${sun.getFullYear()}`;
    }
    return `${monthNames[anchor.getMonth()]} ${anchor.getFullYear()}`;
  }, [view, anchor, monthNames]);

  // Navigacija: u mjesečnom prikazu chevroni nose naziv susjednog mjeseca;
  // u sedmičnom strelice ostaju čiste (period nosi naslov stranice).
  const { prevLabel, nextLabel } = useMemo(() => {
    if (view === "month") {
      const prev = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
      const next = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
      return {
        prevLabel: monthNames[prev.getMonth()],
        nextLabel: monthNames[next.getMonth()],
      };
    }
    return { prevLabel: null, nextLabel: null };
  }, [view, anchor, monthNames]);

  const showTherapist = therapistId === null;
  const selectedSessions = selectedDate ? sessionsByDate[fmtDate(selectedDate)] || [] : [];

  return (
    <div className="relative mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1
            className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {title}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("admin.calendar.subtitle")}
          </p>
        </div>

        <TherapistPicker
          value={therapistId}
          onChange={setTherapistId}
          allLabel={t("admin.calendar.allTherapists")}
          searchPlaceholder={t("admin.calendar.searchTherapist")}
        />
      </div>

      <CalendarSummary
        sessions={sessions}
        selectedSessions={selectedSessions}
        selectedDate={selectedDate}
        isLoading={isLoading}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 items-center gap-3">
          <CalendarDots className="size-5 shrink-0 text-primary" />
          <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
            {t("calendar.scheduleView")}
          </h2>
          {isLoading && <div className="size-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />}
        </div>

        <div className="flex flex-wrap items-center gap-2.5 sm:ml-auto">
          {/* Prikaz: sedmica / mjesec + Danas */}
          <div className="flex h-10 items-center gap-1 rounded-xl border border-border/70 bg-card/60 p-1 shadow-sm">
            {(["week", "month"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                aria-pressed={view === v}
                className={cn(
                  "h-full rounded-lg px-4 text-sm font-medium transition-all duration-200",
                  view === v
                    ? "bg-primary text-primary-foreground shadow-sm shadow-primary/25"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {t(`calendar.${v}`)}
              </button>
            ))}
            <div className="mx-0.5 h-5 w-px bg-border/70" />
            <button
              onClick={goToday}
              className="h-full rounded-lg px-4 text-sm font-medium text-muted-foreground transition-all duration-200 hover:bg-muted/60 hover:text-foreground"
            >
              {t("calendar.today")}
            </button>
          </div>

          {/* Navigacija: ‹ prethodni mjesec · sljedeći mjesec › */}
          <div className="flex h-10 items-stretch overflow-hidden rounded-xl border border-border/70 bg-card/60 shadow-sm">
            <button
              onClick={goBack}
              aria-label={t("calendar.previous")}
              className="group flex h-full items-center gap-1.5 px-3.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
            >
              <CaretLeft className="size-4 shrink-0 transition-transform group-hover:-translate-x-0.5" weight="bold" />
              {prevLabel && <span>{prevLabel}</span>}
            </button>
            <div className="w-px self-stretch bg-border/70" />
            <button
              onClick={goForward}
              aria-label={t("calendar.next")}
              className="group flex h-full items-center gap-1.5 px-3.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
            >
              {nextLabel && <span>{nextLabel}</span>}
              <CaretRight className="size-4 shrink-0 transition-transform group-hover:translate-x-0.5" weight="bold" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
        <div className="min-w-0 flex-1">
          <CalendarGrid
            days={days}
            view={view}
            anchor={anchor}
            today={today}
            selectedDate={selectedDate}
            sessionsByDate={sessionsByDate}
            onSelectDate={setSelectedDate}
            showTherapist={showTherapist}
            dayMarkers={dayMarkers}
          />
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-border/50 bg-card px-3 py-2 text-[11px] text-muted-foreground shadow-sm">
            {Object.entries(STATUS_STYLES).map(([key, style]) => (
              <div key={key} className="flex items-center gap-1.5">
                <span className={cn("size-2 rounded-full", style.dot)} />
                <span>{t(STATUS_KEYS[key])}</span>
              </div>
            ))}
            {dayMarkers && (
              <>
                <span className="h-3 w-px bg-border/70" />
                {DAY_MARKER_LEGEND.map((m) => (
                  <div key={m.kind} className="flex items-center gap-1.5">
                    <span className={cn("size-2 rounded-full", m.dot)} />
                    <span>{m.label}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        <DaySidebar
          selectedDate={selectedDate}
          sessions={selectedSessions}
          dayNames={dayNames}
          monthNames={monthNames}
          showTherapist={showTherapist}
          markers={selectedDate ? dayMarkers?.[fmtDate(selectedDate)] : undefined}
          onCancel={setCancelingSession}
          onEditMembers={setMembersSession}
          allowEmergencyCancel
        />
      </div>

      {membersSession && (
        <SessionMembersDialog
          session={membersSession}
          onClose={() => setMembersSession(null)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ["admin", "calendar"] })}
        />
      )}

      {cancelingSession && (
        <CancelSessionDialog
          session={cancelingSession}
          showFeeOption
          onClose={() => setCancelingSession(null)}
          onConfirm={({ reason, applyFee }) => cancelMutation.mutate({ id: cancelingSession.id, reason, applyFee })}
          isPending={cancelMutation.isPending}
        />
      )}
    </div>
  );
}
